import { createRouter } from "@tanstack/solid-router";
import { rootRoute } from "@/routes/__root";
import { NotFoundRoute } from "@/components/not-found-route";
import { authRoute } from "@/routes/_auth/route";
import { loginRoute } from "@/routes/_auth/login";
import { registerRoute } from "@/routes/_auth/register";
import { forgotPasswordRoute } from "@/routes/_auth/forgot-password";
import { resetPasswordRoute } from "@/routes/_auth/reset-password";
import { verifyEmailRoute } from "@/routes/_auth/verify-email";
import { socialCallbackRoute } from "@/routes/_auth/social-callback";
import { appRoute } from "@/routes/_app/route";
import { indexRoute } from "@/routes/_app/index";
import { dayRoute } from "@/routes/_app/day.$date";
import { entryDetailRoute } from "@/routes/_app/entry.$entryId";
import { calendarRoute } from "@/routes/_app/calendar";
import { searchRoute } from "@/routes/_app/search";
import { favoritesRoute } from "@/routes/_app/favorites";
import { trashRoute } from "@/routes/_app/trash";
import { tagsRoute } from "@/routes/_app/tags";
import { pluginRoutes } from "@/composition";
import { statsRoute } from "@/routes/_app/stats";
import { moodRoute } from "@/routes/_app/mood";
import { profileRoute } from "@/routes/_app/profile";
import { debugRoute } from "@/routes/_app/debug";
import { organizationsRoute } from "@/routes/_app/organizations";
import { organizationDetailRoute } from "@/routes/_app/organizations.$orgId";
import { invitationsRoute } from "@/routes/_app/invitations";
import { themeGalleryRoute } from "@/routes/_app/themes";
import { languageRoute } from "@/routes/_app/language";
import { profileFontsRoute } from "@/routes/_app/profile.fonts";
import { profileShortcutsRoute } from "@/routes/_app/profile.shortcuts";
import { profileDeleteAccountRoute } from "@/routes/_app/profile.delete-account";
import { profileTransferSendRoute } from "@/routes/_app/profile.transfer-send";
import { profileTransferReceiveRoute } from "@/routes/_app/profile.transfer-receive";

const routeTree = rootRoute.addChildren([
  socialCallbackRoute,
  authRoute.addChildren([
    loginRoute,
    registerRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    verifyEmailRoute,
  ]),
  appRoute.addChildren([
    indexRoute, // path: "/" — Hoy (escribir + entradas del día)
    dayRoute, // path: "/day/$date" — día concreto del calendario
    entryDetailRoute, // path: "/entry/$entryId" — detalle/edición
    calendarRoute,
    searchRoute,
    favoritesRoute,
    trashRoute,
    ...pluginRoutes,
    tagsRoute,
    statsRoute,
    moodRoute,
    organizationsRoute,
    organizationDetailRoute,
    invitationsRoute,
    themeGalleryRoute,
    languageRoute,
    profileRoute,
    profileFontsRoute,
    profileShortcutsRoute,
    profileDeleteAccountRoute,
    profileTransferSendRoute,
    profileTransferReceiveRoute,
    debugRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  // Any unknown URL shows the "not found" state (with providers
  // available) instead of a blank screen.
  defaultNotFoundComponent: NotFoundRoute,
  // "intent" preloads only when the user expresses intent (hover/focus on a
  // <Link>). Eager viewport preloading saturated the main thread after login
  // in the template — hover-only is the calm default for Journal too.
  defaultPreload: "intent",
  // oxlint-disable-next-line eslint(no-magic-numbers)
  defaultPreloadStaleTime: 30_000, // 30 s — don't re-preload the same route within 30 s
});

declare module "@tanstack/solid-router" {
  interface Register {
    router: typeof router;
  }
}
